import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { existsFilter } from '@iotv/core-services';
import { classDerivedDeviceEnabledTypes } from '@iotv/datamodel';
import { BranchingQueryDefinition, Customer, getParentEdgeTypeId } from '@iotv/datamodel-core';
import { ComparisonType } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes } from '@iotv/payload-definition';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Tab } from '@mui/material';
import { useEffect, useState } from 'react';
import { v4 } from 'uuid';
import { getPropertyAreaPlaceGatewayQuery, getPropertyAreaPlaceQuery } from '../../../QueryDefinitions';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getAdjacent2Wrapper } from '../../../data/daoFunctions/daoFunctions';
import { extractViewObjectFromNodeEdgeMapper } from '../../../data/daoFunctions/nodeEdgeMapperTransform';
import { useTabHandlerStringKeys } from '../../../hooks/useTabHandler';
import { AdjacentType, Severity } from '../../../types/AppTypes';
import { ObjectHeader } from '../../../v2/components/generic/ObjectView/ObjectHeader';
import { GatewayListViewDefintion } from '../../Gateway/commonGatewayViewDefinition';
import IndirectlyRelatedItemTab from '../../generic/IndirectlyRelatedItemsTab';
import { AreaPointsTab } from '../Area/AreaPointsTab';
import { KMLImportTab } from '../Area/KMLImportTab';
import { AutoTab } from '../AutoTab';
import { getAreaViewDefinition, getPlaceViewDefinition } from '../viewDefinitions';
import { PropertyCoverageView } from './PropertyCoverageView';
import { PropertyDetailView } from './PropertyDetailView';
const { REACT_APP_DEBUG } = process.env;
const debug = REACT_APP_DEBUG && false;
export const propertyToAreaPlaceEntityBranchingQuery = getPropertyAreaPlaceQuery(classDerivedDeviceEnabledTypes
    .map((objectTypeId) => {
    let res = null;
    const edgeTypeId = getParentEdgeTypeId('Place', objectTypeId);
    if (edgeTypeId)
        res = new BranchingQueryDefinition({ id: `Place-${objectTypeId}`, path: [{ objectTypeId, adjacencyType: AdjacentType.PARENT, edgeTypeId }] });
    return res;
})
    .filter(existsFilter));
export const PropertyView = (props) => {
    const property = props.viewObject.matchedPrimary;
    const { transactionFunctions, userFunctions, contextCustomer, applicationData, allowedActions } = props;
    const viewObject = applicationData && property ? extractViewObjectFromNodeEdgeMapper(applicationData, property) : props.viewObject;
    /**
     * Gets all necessary things related to this property - areas, places, entities within either (if any)
     *
     * Then add to app state
     *
     * Runs on property change
     */
    const getAreaPlaceContext = async () => {
        if (!property)
            return;
        const { err, data } = await ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [property],
            query: propertyToAreaPlaceEntityBranchingQuery,
        });
        if (err || !data) {
            userFunctions.setUserMessage({
                id: v4(),
                type: 'MessageBar',
                severity: Severity.error,
                label: 'PropertyView',
                content: err ? `${err.message}` : 'No response',
            });
        }
        if (data) {
            debug && console.log('PropertyView: getAreaPlaceContext returned', data.getSummary());
            userFunctions.addObjectsToState(data.getContainedNodesAndEdges());
        }
    };
    useEffect(() => {
        getAreaPlaceContext();
    }, [property?.sk]);
    /** Get most proximate customer */
    const [mostProximateCustomer, setMostProximateCustomer] = useState(undefined);
    useEffect(() => {
        if (!property)
            return;
        const request = {
            scopeDefinitingContextObject: property,
            adjacencyType: AdjacentType.PARENT,
            objectTypeId: 'Customer',
            edgeTypeId: getParentEdgeTypeId('Property', 'Customer'),
        };
        getAdjacent2Wrapper(request).then((res) => {
            debug && console.log('PropertyView res 2', res);
            if (res.data?.Items) {
                const customers = res.data.Items.filter((vob) => vob.type === 'Customer').map((vob) => new Customer(vob));
                userFunctions.addObjectsToState(res.data.Items);
                setMostProximateCustomer(customers.at(0));
            }
        });
    }, [property?.sk]);
    /** Table Set Up */
    const tabDefs = [
        {
            label: 'Property',
            value: 'Property',
            component: property ? _jsx(PropertyDetailView, { ...props, contextObject: property }) : _jsx(_Fragment, {}),
        },
        { direction: AdjacentType.CHILD, label: 'Areas', value: 'Areas', type: 'Area' },
        { direction: AdjacentType.CHILD, label: 'Places', value: 'Places', type: 'Place' },
        { label: 'Coverage', value: 'Coverage', component: property ? _jsx(PropertyCoverageView, { property: property }) : _jsx(_Fragment, {}) },
        {
            label: 'Groups',
            value: 'Groups',
            type: 'Group',
            allOrAny: 'any',
            constraints: Object.keys(deviceEnabledTypes).map((deviceEnabledType) => ({
                key: 'supportedType',
                predicate: ComparisonType.EQUALS,
                value: deviceEnabledType,
            })),
        },
        {
            label: 'Gateways',
            value: 'Gateways',
            component: property ? (_jsx(IndirectlyRelatedItemTab, { targetObjectTypeId: 'Gateway',
                viewDefinition: GatewayListViewDefintion,
                branchingQueryDefinition: getPropertyAreaPlaceGatewayQuery(),
                resolvedContextObject: property,
                tabValue: 'Gateways' })) : null,
        },
        property && property.points?.length
            ? {
                label: 'Points',
                value: 'Points',
                component: _jsx(AreaPointsTab, { contextArea: property, userFunctions, viewOnly: allowedActions?.edit !== true }),
            }
            : null,
        {
            label: 'KML Import',
            value: 'kmlImport',
            component: property ? (_jsx(KMLImportTab, { contextObject: property,
                childAreas: applicationData?.getChildNodes(property).filter((n) => n.type === 'Area'),
                childPlaces: applicationData?.getChildNodes(property).filter((n) => n.type === 'Place'),
                userFunctions,
                viewOnly: allowedActions?.edit !== true })) : null,
        },
    ];
    const tabKeyMap = Object.fromEntries(tabDefs.filter(existsFilter).map((t) => [t.value, t]));
    const { state: [[value, setValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabKeyMap);
    const typesToViewDefs = {
        Area: getAreaViewDefinition(),
        Place: getPlaceViewDefinition(),
    };
    return (_jsxs(_Fragment, { children: [_jsx(ObjectHeader, { vob: property }), _jsxs(TabContext, { value: value, children: [_jsx(TabList, { onChange: handleTabChange, children: tabDefs.filter(existsFilter).map((tabDef, i) => (_jsx(Tab, { value: tabDef.value, label: tabDef.label, disabled: false }, `tabHeader_${i}`))) }), tabDefs.filter(existsFilter).map((tabDef, index) => (_jsx(TabPanel, { value: tabDef.value, sx: { padding: 0, paddingBlockStart: 1 }, children: property && (_jsx(AutoTab, { tabDef: tabDef,
                            mostProximateCustomer: mostProximateCustomer,
                            contextCustomer,
                            contextObject: property,
                            viewObject,
                            userFunctions,
                            transactionFunctions,
                            viewDefinitionIn: typesToViewDefs[tabDef.type ?? ''],
                            newObjectDefinition: typesToViewDefs[tabDef.type ?? ''] })) }, `tabBase_${index}`)))] })] }));
};
